import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useMemo, useState } from "react";
import { getQuestionDisplayMeta } from "./catalogModel";
import type {
  CatalogQuestion,
  FlatLibraryQuestion,
  PreviewKind
} from "./catalogTypes";
import type { InstrumentDraft, QuestionOverride } from "./types";
import { compactFieldSx } from "./formStyles";

/**
 * Did the user actually change anything from the catalog defaults? When a
 * catalog question is added, we seed the override with the existing
 * `selfAssessmentPrompt` / `helpText` so the SysAdmin can see them in the
 * editor — but that seed shouldn't count as an "edit". Compare each populated
 * override field against the catalog source.
 */
function isOverrideUserEdited(
  override: QuestionOverride | undefined,
  catalogRow: CatalogQuestion | undefined,
  isCustom: boolean
): boolean {
  if (isCustom) return true;
  if (!override) return false;
  if (!catalogRow) return Object.keys(override).length > 0;
  const norm = (s: string | null | undefined) => (s ?? "").trim();
  // `options` and `type` are never seeded on a catalog add, so any presence
  // implies a user edit.
  if (override.options !== undefined) return true;
  if (override.type !== undefined) return true;
  if (override.title !== undefined && norm(override.title) !== norm(catalogRow.title)) return true;
  if (
    override.prompt !== undefined &&
    norm(override.prompt) !== norm(catalogRow.defaultPrompt)
  ) {
    return true;
  }
  if (
    override.selfAssessmentPrompt !== undefined &&
    norm(override.selfAssessmentPrompt) !== norm(catalogRow.selfAssessmentPrompt)
  ) {
    return true;
  }
  if (
    override.helpText !== undefined &&
    norm(override.helpText) !== norm(catalogRow.helpText)
  ) {
    return true;
  }
  return false;
}

type Props = {
  instrument: InstrumentDraft;
  flatLibrary: FlatLibraryQuestion[];
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  catalogQuestionsById: Map<string, CatalogQuestion>;
  selectedIds: string[];
  requiredById: Record<string, boolean>;
  answerSetById: Record<string, string>;
  overridesById: Record<string, QuestionOverride>;
  onChange: (next: {
    selectedIds: string[];
    requiredById: Record<string, boolean>;
    answerSetById: Record<string, string>;
    overridesById: Record<string, QuestionOverride>;
  }) => void;
};

export function QuestionsEditor({
  instrument,
  flatLibrary,
  flatByQuestionId,
  catalogQuestionsById,
  selectedIds,
  requiredById,
  answerSetById,
  overridesById,
  onChange
}: Props) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | PreviewKind>("all");
  // Question-edit dialog. `addCustom` mode means we're creating a NEW custom
  // question and won't add it to the module unless the user clicks Done.
  type DialogState =
    | { mode: "edit"; id: string }
    | { mode: "addCustom"; tempId: string };
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dialogDraft, setDialogDraft] = useState<QuestionOverride>({});

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visibleLibrary = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return flatLibrary.filter((q) => {
      if (filter !== "all" && q.kind !== filter) return false;
      if (needle && !q.searchBlob.includes(needle)) return false;
      return true;
    });
  }, [flatLibrary, filter, search]);

  const selfAsmtRequired = instrument.assessmentSettings.requireSelfAssessment;

  function update(partial: Partial<{
    selectedIds: string[];
    requiredById: Record<string, boolean>;
    answerSetById: Record<string, string>;
    overridesById: Record<string, QuestionOverride>;
  }>) {
    onChange({
      selectedIds,
      requiredById,
      answerSetById,
      overridesById,
      ...partial
    });
  }

  const addLibraryQuestion = (q: FlatLibraryQuestion) => {
    if (selectedSet.has(q.questionId)) return;
    const nextRequired = { ...requiredById };
    if (q.requiredDefault !== null) nextRequired[q.questionId] = !!q.requiredDefault;
    const nextOverrides = { ...overridesById };
    const catalogRow = catalogQuestionsById.get(q.questionId);
    if (catalogRow) {
      const seed: QuestionOverride = {};
      if (catalogRow.selfAssessmentPrompt && catalogRow.selfAssessmentPrompt.trim()) {
        seed.selfAssessmentPrompt = catalogRow.selfAssessmentPrompt;
      }
      if (catalogRow.helpText && catalogRow.helpText.trim()) {
        seed.helpText = catalogRow.helpText;
      }
      if (Object.keys(seed).length > 0) {
        nextOverrides[q.questionId] = seed;
      }
    }
    // When the catalog row has multiple answer-set variants, lock in the
    // top-usage variant on add. answerSetVariants is sorted by usage
    // descending, so [0] is the most-used set. Makes the "user chose this"
    // intent explicit in the draft instead of relying on the implicit
    // "no entry => variant 0" fallback at render/export time.
    const nextAnswerSet = { ...answerSetById };
    if (q.answerSetVariants.length > 1 && q.answerSetVariants[0]) {
      nextAnswerSet[q.questionId] = q.answerSetVariants[0].id;
    }
    update({
      selectedIds: [...selectedIds, q.questionId],
      requiredById: nextRequired,
      answerSetById: nextAnswerSet,
      overridesById: nextOverrides
    });
  };

  const removeQuestion = (id: string) => {
    const nextSelected = selectedIds.filter((x) => x !== id);
    const nextRequired = { ...requiredById };
    delete nextRequired[id];
    const nextAnswerSet = { ...answerSetById };
    delete nextAnswerSet[id];
    const nextOverrides = { ...overridesById };
    delete nextOverrides[id];
    update({
      selectedIds: nextSelected,
      requiredById: nextRequired,
      answerSetById: nextAnswerSet,
      overridesById: nextOverrides
    });
  };

  const moveQuestion = (id: string, direction: -1 | 1) => {
    const idx = selectedIds.indexOf(id);
    if (idx < 0) return;
    const target = idx + direction;
    if (target < 0 || target >= selectedIds.length) return;
    const next = [...selectedIds];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved!);
    update({ selectedIds: next });
  };

  const toggleRequired = (id: string) => {
    update({
      requiredById: { ...requiredById, [id]: !(requiredById[id] ?? true) }
    });
  };

  function openEditDialog(id: string) {
    setDialog({ mode: "edit", id });
    setDialogDraft(overridesById[id] ?? {});
  }

  function openAddCustomDialog() {
    const tempId = `custom-${Math.random().toString(36).slice(2, 10)}`;
    setDialog({ mode: "addCustom", tempId });
    setDialogDraft({ title: "", prompt: "", type: "radio", options: [] });
  }

  function cancelDialog() {
    setDialog(null);
    setDialogDraft({});
  }

  function saveDialog() {
    if (!dialog) return;
    if (dialog.mode === "addCustom") {
      update({
        selectedIds: [...selectedIds, dialog.tempId],
        requiredById: { ...requiredById, [dialog.tempId]: true },
        overridesById: { ...overridesById, [dialog.tempId]: dialogDraft }
      });
    } else {
      update({
        overridesById: { ...overridesById, [dialog.id]: dialogDraft }
      });
    }
    setDialog(null);
    setDialogDraft({});
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.6fr) minmax(0, 1fr)" },
        gap: 2,
        alignItems: "start"
      }}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Question library
        </Typography>
        <TextField
          size="small"
          placeholder="Search title, prompt, options…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={compactFieldSx}
        />
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {(["all", "radio", "multi", "feedback"] as const).map((f) => (
            <Chip
              key={f}
              label={f === "all" ? "All" : f === "radio" ? "Radio" : f === "multi" ? "Multi" : "Feedback"}
              size="small"
              variant={filter === f ? "filled" : "outlined"}
              color={filter === f ? "primary" : "default"}
              onClick={() => setFilter(f)}
            />
          ))}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
            {visibleLibrary.length} of {flatLibrary.length}
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxHeight: { md: "calc(100vh - 240px)" },
            overflowY: "auto",
            pr: 0.5
          }}
        >
          {visibleLibrary.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No matching questions.
            </Alert>
          ) : (
            visibleLibrary.map((q) => (
              <LibraryCard
                key={q.questionId}
                q={q}
                selected={selectedSet.has(q.questionId)}
                onAdd={() => addLibraryQuestion(q)}
              />
            ))
          )}
        </Box>
      </Stack>

      <Stack spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ rowGap: 0.5, flexWrap: "wrap" }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Selected
          </Typography>
          <Chip size="small" label={`${selectedIds.length}`} />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={openAddCustomDialog}
            sx={{ whiteSpace: "nowrap" }}
          >
            Custom blank
          </Button>
        </Stack>

        {selectedIds.length === 0 ? (
          <Box
            sx={{
              p: 2,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 1,
              textAlign: "center",
              color: "text.secondary"
            }}
          >
            Add questions from the library on the left, or create a custom blank question.
          </Box>
        ) : (
          <Stack spacing={1}>
            {selectedIds.map((id, idx) => {
              const isCustom = id.startsWith("custom-");
              return (
                <SelectedCard
                  key={id}
                  id={id}
                  index={idx}
                  total={selectedIds.length}
                  isCustom={isCustom}
                  isFirst={idx === 0}
                  isLast={idx === selectedIds.length - 1}
                  required={requiredById[id] ?? true}
                  override={overridesById[id]}
                  variantId={answerSetById[id]}
                  isEdited={isOverrideUserEdited(
                    overridesById[id],
                    catalogQuestionsById.get(id),
                    isCustom
                  )}
                  flatByQuestionId={flatByQuestionId}
                  onEdit={() => openEditDialog(id)}
                  onToggleRequired={() => toggleRequired(id)}
                  onSelectVariant={(variantId) =>
                    update({
                      answerSetById: { ...answerSetById, [id]: variantId }
                    })
                  }
                  onMoveUp={() => moveQuestion(id, -1)}
                  onMoveDown={() => moveQuestion(id, 1)}
                  onRemove={() => removeQuestion(id)}
                />
              );
            })}
          </Stack>
        )}
      </Stack>

      <QuestionEditDialog
        dialog={dialog}
        draft={dialogDraft}
        setDraft={setDialogDraft}
        selfAsmtRequired={selfAsmtRequired}
        flatByQuestionId={flatByQuestionId}
        variantId={dialog?.mode === "edit" ? answerSetById[dialog.id] : undefined}
        onCancel={cancelDialog}
        onSave={saveDialog}
      />
    </Box>
  );
}

function LibraryCard({
  q,
  selected,
  onAdd
}: {
  q: FlatLibraryQuestion;
  selected: boolean;
  onAdd: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        borderColor: selected ? "success.main" : "divider",
        bgcolor: selected ? "success.50" : "background.paper",
        position: "relative"
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {q.title}
            </Typography>
            <Chip
              size="small"
              label={q.kind === "feedback" ? "Feedback" : q.kind === "multi" ? "Multi" : "Radio"}
              variant="outlined"
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
            {q.usageActivityCount > 0 ? (
              <Typography variant="caption" color="text.secondary">
                Used in {q.usageActivityCount} activit{q.usageActivityCount === 1 ? "y" : "ies"}
              </Typography>
            ) : null}
          </Stack>
          {q.prompt ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              {q.prompt}
            </Typography>
          ) : null}
          {q.kind !== "feedback" && q.answers.length > 0 ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              {q.answers.slice(0, 4).map((a, i) => (
                <Chip
                  key={`${a.label}-${i}`}
                  label={a.label}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: "0.65rem" }}
                />
              ))}
              {q.answers.length > 4 ? (
                <Typography variant="caption" color="text.secondary">
                  +{q.answers.length - 4}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Box>
        <Tooltip title={selected ? "Already added" : "Add to module"}>
          <span>
            <IconButton
              size="small"
              onClick={onAdd}
              disabled={selected}
              color={selected ? "success" : "primary"}
            >
              {selected ? <CheckCircleIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

function SelectedCard({
  id,
  index,
  isFirst,
  isLast,
  isCustom,
  required,
  override,
  variantId,
  isEdited,
  flatByQuestionId,
  onEdit,
  onToggleRequired,
  onSelectVariant,
  onMoveUp,
  onMoveDown,
  onRemove
}: {
  id: string;
  index: number;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  isCustom: boolean;
  required: boolean;
  override: QuestionOverride | undefined;
  variantId: string | undefined;
  isEdited: boolean;
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  onEdit: () => void;
  onToggleRequired: () => void;
  onSelectVariant: (variantId: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}) {
  const meta = getQuestionDisplayMeta(
    id,
    flatByQuestionId,
    override ? { [id]: override } : {},
    variantId
  );
  // Only catalog questions ever expose a multi-variant picker. Custom
  // questions define their own options in the dialog and never have variants
  // to choose between. We also gate on >1 variant so single-variant
  // questions don't render an inert dropdown.
  const libraryRow = !isCustom ? flatByQuestionId.get(id) : undefined;
  const variants = libraryRow?.answerSetVariants ?? [];
  const showVariantPicker = variants.length > 1;
  const selectedVariantId =
    variantId ?? (variants[0]?.id ?? "");
  const selectedVariant =
    variants.find((v) => v.id === selectedVariantId) ?? variants[0];
  // Popover state for the variant picker. Anchored to the small button under
  // the prompt; opened on click. Inline because each card needs its own
  // anchor instance.
  const [variantAnchorEl, setVariantAnchorEl] = useState<HTMLElement | null>(null);
  const variantPickerOpen = Boolean(variantAnchorEl);

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Stack spacing={0} sx={{ alignItems: "center" }}>
          <IconButton size="small" disabled={isFirst} onClick={onMoveUp}>
            <ArrowUpwardIcon fontSize="inherit" sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography variant="caption" color="text.secondary">
            {index + 1}
          </Typography>
          <IconButton size="small" disabled={isLast} onClick={onMoveDown}>
            <ArrowDownwardIcon fontSize="inherit" sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onEdit}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {meta.title || (isCustom ? "Custom question" : id)}
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={meta.kind === "feedback" ? "Feedback" : meta.kind === "multi" ? "Multi" : "Radio"}
              sx={{ height: 18, fontSize: "0.65rem" }}
            />
            {isCustom ? (
              <Chip size="small" color="secondary" variant="outlined" label="Custom" sx={{ height: 18, fontSize: "0.65rem" }} />
            ) : null}
            {isEdited ? (
              <Chip size="small" color="warning" variant="outlined" label="Edited" sx={{ height: 18, fontSize: "0.65rem" }} />
            ) : null}
          </Stack>
          {meta.prompt ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {meta.prompt}
            </Typography>
          ) : null}
          {showVariantPicker ? (
            <Box
              sx={{ mt: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setVariantAnchorEl(e.currentTarget)}
                sx={{
                  textTransform: "none",
                  justifyContent: "space-between",
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  py: 0.25
                }}
              >
                Answer scale ({selectedVariant?.answers.length ?? 0} option
                {(selectedVariant?.answers.length ?? 0) === 1 ? "" : "s"})
              </Button>
              <Popover
                open={variantPickerOpen}
                anchorEl={variantAnchorEl}
                onClose={() => setVariantAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                slotProps={{ paper: { sx: { maxWidth: 360 } } }}
              >
                <Box sx={{ p: 1.25 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1 }}
                  >
                    Choose the answer scale for this question.
                  </Typography>
                  <Stack spacing={0.75}>
                    {variants.map((v) => {
                      const isSelected = v.id === selectedVariantId;
                      return (
                        <Paper
                          key={v.id}
                          variant="outlined"
                          onClick={() => {
                            if (!isSelected) onSelectVariant(v.id);
                            setVariantAnchorEl(null);
                          }}
                          sx={{
                            p: 1,
                            cursor: "pointer",
                            borderColor: isSelected ? "primary.main" : "divider",
                            bgcolor: isSelected ? "action.selected" : "background.paper",
                            "&:hover": {
                              bgcolor: isSelected ? "action.selected" : "action.hover"
                            }
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            sx={{ mb: 0.5 }}
                          >
                            {isSelected ? (
                              <CheckCircleIcon
                                color="primary"
                                sx={{ fontSize: 14 }}
                              />
                            ) : null}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              {v.answers.length} option
                              {v.answers.length === 1 ? "" : "s"}
                            </Typography>
                            {v.usageActivityCount > 0 ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                · used by {v.usageActivityCount} activit
                                {v.usageActivityCount === 1 ? "y" : "ies"}
                              </Typography>
                            ) : null}
                          </Stack>
                          <Stack spacing={0.25}>
                            {v.answers.map((a, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                sx={{
                                  fontSize: "0.78rem",
                                  lineHeight: 1.35,
                                  wordBreak: "break-word"
                                }}
                              >
                                • {a.label}
                              </Typography>
                            ))}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Box>
              </Popover>
            </Box>
          ) : null}
        </Box>
        <Tooltip title={required ? "Required (click to make optional)" : "Optional (click to make required)"}>
          <Switch size="small" checked={required} onChange={onToggleRequired} />
        </Tooltip>
        <Tooltip title="Edit question">
          <IconButton size="small" onClick={onEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove from module">
          <IconButton size="small" onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// -------- Edit dialog --------

type DialogStateProp =
  | { mode: "edit"; id: string }
  | { mode: "addCustom"; tempId: string }
  | null;

function QuestionEditDialog({
  dialog,
  draft,
  setDraft,
  selfAsmtRequired,
  flatByQuestionId,
  variantId,
  onCancel,
  onSave
}: {
  dialog: DialogStateProp;
  draft: QuestionOverride;
  setDraft: (next: QuestionOverride) => void;
  selfAsmtRequired: boolean;
  flatByQuestionId: Map<string, FlatLibraryQuestion>;
  variantId: string | undefined;
  onCancel: () => void;
  onSave: () => void;
}) {
  const open = dialog !== null;
  const isCustom =
    dialog?.mode === "addCustom" || (dialog?.mode === "edit" && dialog.id.startsWith("custom-"));
  const dialogId = dialog?.mode === "edit" ? dialog.id : (dialog?.mode === "addCustom" ? dialog.tempId : "");

  // Resolved meta off the live working draft for in-dialog preview labels.
  // Carry the variant id through so the default answers shown for an
  // unoverridden catalog question reflect the user's selected scale.
  const meta = useMemo(
    () =>
      getQuestionDisplayMeta(
        dialogId || "preview",
        flatByQuestionId,
        dialogId ? { [dialogId]: draft } : {},
        variantId
      ),
    [dialogId, flatByQuestionId, draft, variantId]
  );

  const resolvedOptions = draft.options
    ? draft.options
    : meta.answers.map((a) => ({
        label: a.label,
        value: a.value,
        helpText: a.prompt ?? ""
      }));

  function patch(p: Partial<QuestionOverride>) {
    setDraft({ ...draft, ...p });
  }
  function commitOptions(next: { label: string; value: string; helpText?: string }[]) {
    setDraft({ ...draft, options: next });
  }
  function updateOptionRow(idx: number, p: Partial<{ label: string; value: string; helpText: string }>) {
    commitOptions(resolvedOptions.map((row, i) => (i === idx ? { ...row, ...p } : row)));
  }
  function removeOptionRow(idx: number) {
    commitOptions(resolvedOptions.filter((_, i) => i !== idx));
  }
  function addOptionRow() {
    commitOptions([
      ...resolvedOptions,
      { label: "", value: String(resolvedOptions.length + 1), helpText: "" }
    ]);
  }

  // For "edit" mode we want kind to follow whatever the catalog/override
  // resolves to; for custom, the user picks via the Type select.
  const effectiveKind = isCustom ? draft.type ?? "radio" : meta.kind;

  // Save is allowed only when required fields are filled. For custom
  // questions: title + default prompt. For radio/multi questions (catalog or
  // custom): at least one answer option, and every option must have a
  // non-empty label.
  const hasAnyOption =
    effectiveKind === "feedback" || resolvedOptions.length > 0;
  const allOptionLabelsFilled =
    effectiveKind === "feedback" || resolvedOptions.every((o) => o.label.trim().length > 0);
  const canSave =
    (isCustom
      ? Boolean((draft.title ?? "").trim()) && Boolean((draft.prompt ?? "").trim())
      : true) &&
    hasAnyOption &&
    allOptionLabelsFilled;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {dialog?.mode === "addCustom" ? "Add custom question" : "Edit question"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <TextField
            size="small"
            label="Title"
            fullWidth
            required={isCustom}
            value={draft.title ?? meta.title ?? ""}
            onChange={(e) => patch({ title: e.target.value })}
            sx={compactFieldSx}
          />
          {isCustom ? (
            <TextField
              size="small"
              label="Question type"
              fullWidth
              select
              value={draft.type ?? "radio"}
              onChange={(e) => patch({ type: e.target.value as PreviewKind })}
              sx={compactFieldSx}
            >
              <MenuItem value="radio">Radio (single choice)</MenuItem>
              <MenuItem value="multi">Multi-select</MenuItem>
              <MenuItem value="feedback">Feedback (free text)</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              label="Question type"
              fullWidth
              value={
                meta.kind === "feedback"
                  ? "Feedback (free text)"
                  : meta.kind === "multi"
                    ? "Multi-select"
                    : "Radio (single choice)"
              }
              disabled
              helperText="Type is fixed for catalog questions."
              sx={compactFieldSx}
            />
          )}
          <TextField
            size="small"
            label="Default prompt"
            fullWidth
            multiline
            minRows={2}
            required={isCustom}
            value={draft.prompt ?? meta.prompt ?? ""}
            onChange={(e) => patch({ prompt: e.target.value })}
            sx={compactFieldSx}
          />
          {selfAsmtRequired ? (
            <TextField
              size="small"
              label="Self-assessment prompt (optional)"
              fullWidth
              multiline
              minRows={2}
              value={draft.selfAssessmentPrompt ?? ""}
              onChange={(e) => patch({ selfAssessmentPrompt: e.target.value })}
              sx={compactFieldSx}
            />
          ) : null}
          <TextField
            size="small"
            label="Help text (optional, shown next to the question)"
            fullWidth
            multiline
            minRows={2}
            value={draft.helpText ?? ""}
            onChange={(e) => patch({ helpText: e.target.value })}
            sx={compactFieldSx}
          />
          {effectiveKind !== "feedback" ? (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
              >
                Answer options
              </Typography>
              {resolvedOptions.length === 0 ? (
                <Box
                  sx={{
                    p: 1.25,
                    border: "1px dashed",
                    borderColor: "error.main",
                    borderRadius: 1,
                    color: "error.main",
                    fontSize: "0.8rem",
                    textAlign: "center"
                  }}
                >
                  At least one answer option is required for {effectiveKind === "multi" ? "multi-select" : "radio"} questions. Click "Add option" to add one.
                </Box>
              ) : (
                <Stack spacing={1}>
                  {resolvedOptions.map((opt, idx) => (
                    <Stack
                      key={idx}
                      direction="column"
                      spacing={0.75}
                      sx={{
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.default"
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <TextField
                          size="small"
                          label="Label"
                          required
                          error={!opt.label.trim()}
                          value={opt.label}
                          onChange={(e) => updateOptionRow(idx, { label: e.target.value })}
                          sx={{ ...compactFieldSx, flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          value={opt.value}
                          onChange={(e) => updateOptionRow(idx, { value: e.target.value })}
                          sx={{ ...compactFieldSx, width: 88 }}
                        />
                        <Tooltip title="Remove option">
                          <IconButton size="small" onClick={() => removeOptionRow(idx)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      <TextField
                        size="small"
                        label="Help text (optional)"
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={opt.helpText ?? ""}
                        onChange={(e) => updateOptionRow(idx, { helpText: e.target.value })}
                        sx={compactFieldSx}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
              <Button
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={addOptionRow}
                sx={{ mt: 1 }}
              >
                Add option
              </Button>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={!canSave}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
